/**
 * Keeps the {@link Scene} in step with everything it depends on, whether or not the graph
 * window is open:
 *
 * - the core's graph (new nodes get a place, removed ones go, the layout adapts);
 * - the project's saved positions (applied once per project `generation`, and only after the
 *   graph mirror caught up, so they never land on the previous project's nodes);
 * - the filters (and the groups switch), the selection, the search, the labels, the 2D/3D
 *   choice, and the fit / centre requests.
 *
 * It also sends the positions back to the core ({@link SceneSync.pushPositions}), but never
 * before the project's own positions were applied, so it cannot overwrite them.
 *
 * **One layout for everyone on the same core.** Whoever caused a change leads its layout:
 * the page whose edit it was (`graph.changed` says `by`), or that drags a node. That page runs
 * the simulation and shares the result when it settles; a drag is shared while it happens.
 * The other pages do not lay that change out themselves: they glide to the positions that
 * arrive (`project.nodes_moved`, `scene.follow`). Every page applies every whole layout in the
 * core's order (its own too), so two pages sharing at once still end on the same one. If no
 * positions come for a change (made by a script, with no page to lay it out), a page waits
 * {@link LEADER_WAIT_MS} (up to {@link LEADER_MAX_WAIT_MS} while the person who made it is
 * still here) and then lays it out itself. Personal views (filters, spacing, 3D) re-lay out
 * this page only and share nothing.
 *
 * A page that may not edit (a viewer or commenter in a space) never leads: it only follows,
 * and sends no positions (the core would refuse them).
 */
import { createEffect, on } from "solid-js";
import { unwrap } from "solid-js/store";
import type { GraphMirror } from "../client/graph";
import type { AccessService } from "./access";
import type { PresenceService } from "./presence";
import type { Positions, Project } from "./project";
import { memberPlace, prefersReducedMotion, type Scene, type SceneFilters } from "./scene";
import type { View } from "./view";
import { sameEdge } from "../client/graph";

/**
 * How long a page waits for the layout of a change made by someone who is not here (a script,
 * the CLI, a page that left) before laying it out itself.
 */
export const LEADER_WAIT_MS = 1500;

/**
 * How long a page waits, at most, for the layout of a change made by someone who is here: a
 * big graph can take a few seconds to settle on their page.
 */
export const LEADER_MAX_WAIT_MS = 15_000;

/** What the graph window needs from the sync. */
export interface SceneSync {
  /** Sends the current positions to the core (when this page's layout must be kept). */
  pushPositions(): void;
  /** The layout stopped moving: shares it if this page leads it. */
  settled(): void;
  /** Nodes are being dragged here: this page leads, and the others see them move. */
  dragged(nodes: readonly { id: string; x: number; y: number }[]): void;
  /** A drag ended: this page leads, and shares where everything is. */
  nodeMoved(): void;
}

/** What {@link createSceneSync} follows. Each piece is a separate service. */
export interface SceneSyncDeps {
  scene: Scene;
  graph: GraphMirror;
  view: View;
  project: Project;
  /** Who this page is, to tell its own changes from the others'. */
  presence: PresenceService;
  /** Whether this page may move nodes for everyone (editors and owners). */
  access: AccessService;
}

/** Starts following the graph, the project and the view. Call it inside a Solid root. */
export function createSceneSync({ scene, graph, view, project, presence, access }: SceneSyncDeps): SceneSync {
  let linkCount = -1;
  /** Counts positions received from other people (to know whether a leader answered). */
  let followed = 0;
  /**
   * The environment shown when the project's positions were last applied. Opening a project
   * shows the whole graph for a moment (until its plugins, and so its environments, are known):
   * its positions are applied again for the environment that then shows, which holds them all.
   */
  let appliedScope: string | null = null;
  /** The wait for someone else's layout, if any. */
  let waiting: ReturnType<typeof setTimeout> | undefined;

  /** Whether the last graph change is this page's to lay out (its own, or nobody's known). */
  const mine = (): boolean => {
    if (!mayMove()) return false;
    const by = graph.state.by;
    const you = presence.you();
    return by === null || you === null || by === you;
  };

  /**
   * Someone else changed the graph: wait for their layout (longer while they are still here),
   * then lay it out here if none came.
   */
  function awaitLeader(): void {
    clearTimeout(waiting);
    const before = followed;
    const leader = graph.state.by;
    let waited = 0;
    /** Checks, every {@link LEADER_WAIT_MS}, whether the layout came or will not come. */
    const check = () => {
      waited += LEADER_WAIT_MS;
      if (followed !== before) return;
      const here = presence.people().some((p) => p.id === leader);
      if (here && waited < LEADER_MAX_WAIT_MS) {
        waiting = setTimeout(check, LEADER_WAIT_MS);
        return;
      }
      // Nobody laid it out: lay it out here (shared only by a page that may edit).
      scene.lead = mayMove();
      scene.reheat(0.5);
      scene.redraw = true;
    };
    waiting = setTimeout(check, LEADER_WAIT_MS);
  }

  /** The filters as plain data (a copy, so the effect below sees every change). */
  const filters = (): SceneFilters => ({ hiddenCategories: [...view.state.hiddenCategories], linkKinds: { ...view.state.linkKinds }, groups: view.state.groups, bundles: view.state.bundles });

  /** Whether this page may share positions (it may edit). */
  const mayMove = () => access.can("editor");

  /** Sends positions, once this project's own positions are on screen (if this page may). */
  function pushPositions(): void {
    if (!mayMove() || scene.appliedGeneration !== project.state()?.generation) return;
    project.setPositions(scene.positions());
  }

  /** Places the nodes where the opened project says, once per project generation. */
  function applyProjectPositions(): void {
    const state = project.state();
    const shown = graph.scope() ? `${graph.scope()!.id}/${graph.scope()!.workspace ?? ""}` : null;
    if (!state || !scene.laidOut || (state.generation === scene.appliedGeneration && shown === appliedScope)) return;
    // The answer to "open" can arrive before the event carrying the opened graph.
    if (graph.state.revision < state.saved_revision) return;
    const placed = scene.applyPositions(state.positions);
    if (placed) {
      scene.fit();
      if (placed < scene.nodes.length) scene.reheat(0.3);
    } else if (state.generation > 0 && scene.nodes.length) {
      scene.warm();
    }
    scene.appliedGeneration = state.generation;
    appliedScope = shown;
    scene.redraw = true;
    // The layout may have been computed just now (no movement to wait for): report it.
    pushPositions();
  }

  /**
   * Forgets a selection that no longer exists (deleted, or undone). With `hidden`, a node the
   * filters now hide is forgotten too.
   */
  function dropStaleSelection(hidden = false): void {
    const s = view.state.selection;
    if (s?.type === "node") {
      const node = scene.byId.get(s.id);
      if (!node || (hidden && !node.visible)) view.selectNode(null);
    }
    if (s?.type === "nodes") {
      const kept = s.ids.filter((id) => {
        const node = scene.byId.get(id);
        return node && (!hidden || node.visible);
      });
      if (kept.length !== s.ids.length) view.selectNodes(kept);
    }
    if (s?.type === "group" && !scene.groupById.has(s.id)) view.selectGroup(null);
    if (s?.type === "bundle" && !scene.bundleByKey.has(s.key)) view.selectBundle(null);
    if (s?.type === "link" && !scene.links.some((l) => sameEdge(l.link, s.link))) view.selectLink(null);
  }

  /** Follows the core's graph. */
  function followGraph(): void {
    const g = unwrap(graph.state.graph);
    const { added } = scene.sync(g, filters());
    if (!scene.laidOut && graph.state.loaded) {
      scene.laidOut = true;
      if (!g.nodes.length) {
        // An empty graph: put the world's origin in the middle so new nodes appear in view.
        scene.view.x = scene.width / 2;
        scene.view.y = scene.height / 2;
      } else scene.warm();
    } else if (added || g.links.length !== linkCount) {
      if (mine()) {
        scene.lead = true;
        scene.reheat(prefersReducedMotion() ? 0 : 0.5);
      } else if (scene.dimension === "3d") {
        // The 3D picture is this page's own: it adapts here, and shares nothing.
        scene.lead = false;
        scene.reheat(0.5);
      } else awaitLeader();
    }
    linkCount = g.links.length;
    applyProjectPositions();
    dropStaleSelection();
    scene.redraw = true;
  }

  // Positions shared by anyone (this page's own whole layouts too: everyone applies them in the
  // core's order, so pages sharing at the same time agree). A page still laying out (the change
  // it leads, or a personal view such as the spacing) ignores the echo of its own older layout:
  // it would stop the movement; a page that leads shares again when it settles.
  project.onNodesMoved((moved) => {
    if (!scene.laidOut || scene.appliedGeneration !== project.state()?.generation) return;
    const own = moved.by === presence.you();
    if (own && (!moved.whole || scene.alpha > 0)) return;
    if (!own) followed++;
    scene.follow(moved.positions);
  });

  createEffect(on(() => graph.state.version, followGraph));
  createEffect(on(() => project.state()?.generation, applyProjectPositions, { defer: true }));
  project.providePositions(() => (scene.appliedGeneration === project.state()?.generation ? scene.positions() : null));

  createEffect(
    on(
      filters,
      (f) => {
        scene.applyFilters(f);
        scene.lead = false; // a personal view: re-laid out here only
        scene.reheat(0.4);
        dropStaleSelection(true);
        scene.redraw = true;
      },
      { defer: true },
    ),
  );

  createEffect(() => {
    void view.state.selection;
    void view.state.query;
    void view.state.labels;
    scene.redraw = true;
  });

  // The spacing: set before the first layout, and a change spreads the current one out again.
  createEffect(
    on(
      () => view.state.spacing,
      (spacing, previous) => {
        scene.spacing = spacing;
        if (previous === undefined) return;
        scene.lead = false; // a personal view: re-laid out here only
        scene.reheat(0.8);
        scene.redraw = true;
      },
    ),
  );

  createEffect(
    on(
      () => view.state.dimension,
      (dimension) => {
        scene.setDimension(dimension);
        if (dimension === "3d" && prefersReducedMotion()) scene.warm();
      },
    ),
  );

  // In the diagram the diagram fits and centres (`state/diagram`): the nodal view stays as it was.
  createEffect(on(view.fitRequests, () => view.mode() === "nodal" && ((scene.fit(), (scene.redraw = true))), { defer: true }));
  createEffect(
    on(
      view.centerRequest,
      (request) => {
        if (!request || view.mode() !== "nodal") return;
        scene.centerOn(request.id);
        scene.redraw = true;
      },
      { defer: true },
    ),
  );

  return {
    pushPositions,
    settled() {
      if (scene.lead) pushPositions();
    },
    dragged(nodes) {
      scene.lead = mayMove();
      if (!scene.lead || scene.dimension === "3d" || scene.appliedGeneration !== project.state()?.generation) return;
      const moves: Positions = {};
      for (const n of nodes) {
        const folded = scene.byId.get(n.id)?.fold;
        // A folded group's disc carries its hidden nodes: they are what moves.
        if (folded) for (const m of folded.members) moves[m.id] = memberPlace(scene.byId.get(n.id)!, folded, m.id).map(Math.round) as [number, number];
        else moves[n.id] = [Math.round(n.x), Math.round(n.y)];
      }
      project.moveNodes(moves);
    },
    nodeMoved() {
      scene.lead = mayMove();
      pushPositions();
    },
  };
}
